import {
  Container,
  error,
  GetMessageWithIntl,
  Radio,
  Toast,
} from '~/shared/components';
import { CaptionButton } from '~/shared/components/ui/Button';
import {
  getExceptionMessage,
  getRandomStringId,
  getWorkerExceptionMessage,
  includeInputValidateError,
  RequireInput,
  ViewId,
} from '~/shared/utils';
import './BlueprintBlueprintSearch.css';
import { FileItem, FileUploader } from '~/shared/components/file';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BLUEPRINT_SEARCH_LIMIT,
  BlueprintSearchCondition,
  getBlueprintSearchState,
  saveBlueprintSearchState,
} from '../BlueprintBlueprintSearchResult/util';
import { useAuth } from '~/shared/contexts/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { useBlueprintSearch } from '../BlueprintSearchProvider';
import {
  validateNumberMinMaxOverDigit,
  validateOnlyNumberWithMaxMin,
} from '~/shared/components/parts/validator';
import { useLoading } from '~/shared/contexts/LoadingProvider';
import { Checkbox, Textbox } from '~/shared/components/ui';
import React from 'react';
import { ConditionCheckbox } from '~/tenant/company/pages/CompanySearch/parts/ConditionCheckbox';
import { useIntl } from 'react-intl';
import {
  SegmentConfirmationDialog,
  SegmentConfirmationDialogResult,
} from '~/shared/components/ui/Dialog/SegmentConfirmationDialog';
import { FullMethodName_ListBlueprintDisplayAttributes } from '~/worker';
import { mtechnavi } from '~/shared/libs/clientsdk';
import { BlueprintSegmentNames } from '../../utils';

const VIEW_ID: ViewId = 'BPR_BLUEPRINT_SEARCH';

interface CheckBoxItem {
  id: string;
  displayName: string;
  order?: number;
}
export const BlueprintBlueprintSearch = () => {
  const intl = useIntl();
  const myEmail = useAuth().user?.email ?? '';
  const navi = useNavigate();
  const { search } = useBlueprintSearch();

  const [isOpenSegmentConfirmation, setOpenSegmentConfirmation] =
    useState(false);
  const { showLoading, hideLoading } = useLoading();

  // 以前の検索条件
  const savedConditions = useMemo(
    () => getBlueprintSearchState(VIEW_ID, myEmail),
    [myEmail]
  );

  // 画像検索
  const [filename, setFilename] = useState<string | null | undefined>(
    savedConditions?.file?.filename ?? null
  );
  const [temporaryImage, setTemporaryImage] = useState<
    | mtechnavi.api.assetinventory.CreateTemporaryImagesResponse.ICreatedImage
    | null
    | undefined
  >(savedConditions?.file?.temporaryImage);
  const [temporarySegmentProperties, setTemporarySegmentProperties] = useState<
    mtechnavi.api.blueprint.ITemporarySegmentProperty[] | null | undefined
  >(savedConditions?.file?.segments);
  const [isFront, setFront] = useState(
    savedConditions?.shape?.classNames?.includes(BlueprintSegmentNames.Front)
  );
  const [isRight, setRight] = useState(
    savedConditions?.shape?.classNames?.includes(BlueprintSegmentNames.Right)
  );
  const [isBottom, setBottom] = useState(
    savedConditions?.shape?.classNames?.includes(BlueprintSegmentNames.Bottom)
  );
  const [topLevel, setTopLevel] = useState(savedConditions?.shape?.topX ?? '');
  // 詳細検索
  const [keywords, setKeywords] = useState(
    savedConditions?.keyword?.text ?? ''
  );
  const [matchType, setMatchType] = useState(
    savedConditions?.keyword?.matchType ?? 'or'
  );
  const [leadNumber, setLeadNumber] = useState(
    savedConditions?.amount?.leadNumber?.value ?? ''
  );
  const [leadNumberLower, setLeadNumberLower] = useState(
    savedConditions?.amount?.leadNumber?.tolerance?.lower ?? '0'
  );
  const [leadNumberUpper, setLeadNumberUpper] = useState(
    savedConditions?.amount?.leadNumber?.tolerance?.upper ?? '0'
  );
  const [unit, setUnit] = useState(savedConditions?.size?.unit ?? 'mm');
  const [outerL, setOuterL] = useState(
    savedConditions?.size?.outerL?.value ?? ''
  );
  const [outerLLower, setOuterLLower] = useState(
    savedConditions?.size?.outerL?.tolerance?.lower ?? '0'
  );
  const [outerLUpper, setOuterLUpper] = useState(
    savedConditions?.size?.outerL?.tolerance?.upper ?? '0'
  );
  const [outerW, setOuterW] = useState(
    savedConditions?.size?.outerW?.value ?? ''
  );
  const [outerWLower, setOuterWLower] = useState(
    savedConditions?.size?.outerW?.tolerance?.lower ?? '0'
  );
  const [outerWUpper, setOuterWUpper] = useState(
    savedConditions?.size?.outerW?.tolerance?.upper ?? '0'
  );
  const [outerT, setOuterT] = useState(
    savedConditions?.size?.outerT?.value ?? ''
  );
  const [outerTLower, setOuterTLower] = useState(
    savedConditions?.size?.outerT?.tolerance?.lower ?? '0'
  );
  const [outerTUpper, setOuterTUpper] = useState(
    savedConditions?.size?.outerT?.tolerance?.upper ?? '0'
  );
  const [topCVL, setTopCVL] = useState(
    savedConditions?.size?.topCVL?.value ?? ''
  );
  const [topCVLLower, setTopCVLLower] = useState(
    savedConditions?.size?.topCVL?.tolerance?.lower ?? '0'
  );
  const [topCVLUpper, setTopCVLUpper] = useState(
    savedConditions?.size?.topCVL?.tolerance?.upper ?? '0'
  );

  const [topCVW, setTopCVW] = useState(
    savedConditions?.size?.topCVW?.value ?? ''
  );
  const [topCVWLower, setTopCVWLower] = useState(
    savedConditions?.size?.topCVW?.tolerance?.lower ?? '0'
  );
  const [topCVWUpper, setTopCVWUpper] = useState(
    savedConditions?.size?.topCVW?.tolerance?.upper ?? '0'
  );
  const [secondCVL, setSecondCVL] = useState(
    savedConditions?.size?.secondCVL?.value ?? ''
  );
  const [secondCVLLower, setSecondCVLLower] = useState(
    savedConditions?.size?.secondCVL?.tolerance?.lower ?? '0'
  );
  const [secondCVLUpper, setSecondCVLUpper] = useState(
    savedConditions?.size?.secondCVL?.tolerance?.upper ?? '0'
  );
  const [secondCVW, setSecondCVW] = useState(
    savedConditions?.size?.secondCVW?.value ?? ''
  );
  const [secondCVWLower, setSecondCVWLower] = useState(
    savedConditions?.size?.secondCVW?.tolerance?.lower ?? '0'
  );
  const [secondCVWUpper, setSecondCVWUpper] = useState(
    savedConditions?.size?.secondCVW?.tolerance?.upper ?? '0'
  );
  const [daL, setDaL] = useState(savedConditions?.size?.daL?.value ?? '');
  const [daLLower, setDaLLower] = useState(
    savedConditions?.size?.daL?.tolerance?.lower ?? '0'
  );
  const [daLUpper, setDaLUpper] = useState(
    savedConditions?.size?.daL?.tolerance?.upper ?? '0'
  );
  const [daW, setDaW] = useState(savedConditions?.size?.daW?.value ?? '');
  const [daWLower, setDaWLower] = useState(
    savedConditions?.size?.daW?.tolerance?.lower ?? '0'
  );
  const [daWUpper, setDaWUpper] = useState(
    savedConditions?.size?.daW?.tolerance?.upper ?? '0'
  );
  const [boardWarp, setBoardWarp] = useState(
    savedConditions?.size?.boardWarp?.value ?? ''
  );
  const [boardWarpLower, setBoardWarpLower] = useState(
    savedConditions?.size?.boardWarp?.tolerance?.lower ?? '0'
  );
  const [boardWarpUpper, setBoardWarpUpper] = useState(
    savedConditions?.size?.boardWarp?.tolerance?.upper ?? '0'
  );
  const [srWarp, setSrWarp] = useState(
    savedConditions?.size?.srWarp?.value ?? ''
  );
  const [srWarpLower, setSrWarpLower] = useState(
    savedConditions?.size?.srWarp?.tolerance?.lower ?? '0'
  );
  const [srWarpUpper, setSrWarpUpper] = useState(
    savedConditions?.size?.srWarp?.tolerance?.upper ?? '0'
  );
  const [daWarp, setDaWarp] = useState(
    savedConditions?.size?.daWarp?.value ?? ''
  );
  const [daWarpLower, setDaWarpLower] = useState(
    savedConditions?.size?.daWarp?.tolerance?.lower ?? '0'
  );
  const [daWarpUpper, setDaWarpUpper] = useState(
    savedConditions?.size?.daWarp?.tolerance?.upper ?? '0'
  );
  const [displayAttributes, setDisplayAttributes] = useState<CheckBoxItem[]>(
    []
  );
  const [checkedAttributes, setCheckedAttributes] = useState<CheckBoxItem[]>(
    []
  );

  /** 必須チェックのための `上位` 入力項目への参照 */
  const topLevelRef = useRef(null);

  /** 形状条件操作不可フラグ */
  const disableSegmentCondition = !temporaryImage;

  /** 各セグメントが抽出・指定されているかフラグ */
  const existsFrontSegment = hasSegment(
    temporarySegmentProperties,
    BlueprintSegmentNames.Front
  );
  const existsRightSegment = hasSegment(
    temporarySegmentProperties,
    BlueprintSegmentNames.Right
  );
  const existsBottomSegment = hasSegment(
    temporarySegmentProperties,
    BlueprintSegmentNames.Bottom
  );
  const existsSomeSegments =
    existsFrontSegment || existsRightSegment || existsBottomSegment;

  /** 上位nの必須フラグ */
  const requireTopLevel = isFront || isRight || isBottom;

  /** サイズ・リード数の値と許容誤差バリデーションを作成する */
  const getSizeConditionValidator = () => {
    return validateNumberMinMaxOverDigit(intl, 2, 0, 999999.99);
  };

  /** 初期データの取得 */
  useEffect(() => {
    (async () => {
      try {
        showLoading();
        const displayAttributesRes =
          await window.App.services.ui.worker.filter<mtechnavi.api.blueprint.IListBlueprintDisplayAttributesResponse>(
            {
              action: 'reload',
              fullMethodName: FullMethodName_ListBlueprintDisplayAttributes,
              filter: {},
              sort: [{ order: 'asc' }],
            }
          );
        const displayAttributeList =
          displayAttributesRes?.items.at(0)?.displayAttributes ?? [];
        const setItem = displayAttributeList.map((item) => ({
          id: getRandomStringId(),
          displayName: item.attributeName ?? '',
          originalOrder: item.order,
          initialTarget: item.initialTarget,
        }));
        setDisplayAttributes(setItem);
        if (savedConditions?.attribute?.length) {
          setCheckedAttributes(
            setItem
              ?.filter((item) =>
                savedConditions?.attribute?.includes(item?.displayName ?? '')
              )
              .map((item) => item)
          );
        } else {
          setCheckedAttributes(
            setItem?.filter((item) => item.initialTarget).map((item) => item)
          );
        }
      } catch (err) {
        console.error(err);
        error(getWorkerExceptionMessage(intl, err));
      }
      hideLoading();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ファイルアップロード処理
   *
   * ファイルから検索用の画像リソースを作成しセグメントを抽出する
   */
  const handleFileUpload = async (files: FileItem[]) => {
    const file = files.at(0);
    if (!file || file?.status !== 'OK') {
      return;
    }
    setFilename(file?.file.name);
    try {
      showLoading();
      // アップロードPDFをPNGに変換
      const result = await window.App.services.ui.worker.apiCall({
        actionName: 'createTemporaryImage',
        request: {
          sourceUrl: file.url,
          imagePropertys: [
            {
              name: 'base',
              format: mtechnavi.api.assetinventory.ImageFormat.PNG,
            },
          ],
        },
      });
      const resultCreateImage = result.at(0)?.createdImages?.at(0);
      if (!resultCreateImage?.signedUrl) {
        return;
      }
      setTemporaryImage(resultCreateImage);
      // セグメント抽出
      const resultSegment = await window.App.services.ui.worker.apiCall({
        actionName: 'extractBlueprintImageSegment',
        request: { url: resultCreateImage?.signedUrl },
      });
      const segmentProperties = resultSegment?.at(0)?.temporarySegmentPropertys;
      setTemporarySegmentProperties(segmentProperties);
      // 抽出されなかった面のチェックボックスを選択解除する
      setFront((prev) =>
        hasSegment(segmentProperties, BlueprintSegmentNames.Front)
          ? prev
          : false
      );
      setRight((prev) =>
        hasSegment(segmentProperties, BlueprintSegmentNames.Right)
          ? prev
          : false
      );
      setBottom((prev) =>
        hasSegment(segmentProperties, BlueprintSegmentNames.Bottom)
          ? prev
          : false
      );
      setTopLevel('');
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
    } finally {
      hideLoading();
    }
  };

  /** セグメントの更新 */
  const handleSaveSegment = async (result: SegmentConfirmationDialogResult) => {
    if (!result?.segmentPropertys?.length) {
      setTemporarySegmentProperties([]);
      setFront(false);
      setRight(false);
      setBottom(false);
      setTopLevel('');
      setOpenSegmentConfirmation(false);
      return;
    }
    showLoading();
    try {
      // セグメント確認、保存
      const request: mtechnavi.api.assetinventory.ICreateTemporaryImagesRequest =
        {
          sourceUrl: temporaryImage?.signedUrl,
          imagePropertys: (result?.segmentPropertys ?? []).map((item) => ({
            name: item.segmentName ?? '',
            format: mtechnavi.api.assetinventory.ImageFormat.PNG,
            option: {
              crop: true,
              width: item.width,
              height: item.height,
              top: item.top,
              left: item.left,
            },
          })),
        };
      const resultCreateTempImages =
        (
          await window.App.services.ui.worker.apiCall({
            request: request,
            actionName: 'createTemporaryImage',
          })
        ).at(0)?.createdImages ?? [];

      const newSegmentProperties: mtechnavi.api.blueprint.ITemporarySegmentProperty[] =
        (result?.segmentPropertys ?? []).map((item) => {
          const segmentImage = resultCreateTempImages.find(
            (image) => image.name === item.segmentName
          );
          return {
            url: segmentImage?.signedUrl,
            segmentProperty: {
              ...item,
              assetId: segmentImage?.assetId,
            },
          };
        });
      setTemporarySegmentProperties(newSegmentProperties);
      // 抽出されなかった面のチェックボックスを選択解除する
      setFront((prev) =>
        hasSegment(newSegmentProperties, BlueprintSegmentNames.Front)
          ? prev
          : false
      );
      setRight((prev) =>
        hasSegment(newSegmentProperties, BlueprintSegmentNames.Right)
          ? prev
          : false
      );
      setBottom((prev) =>
        hasSegment(newSegmentProperties, BlueprintSegmentNames.Bottom)
          ? prev
          : false
      );
      setOpenSegmentConfirmation(false);
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
    } finally {
      hideLoading();
    }
  };

  /** 入力エラーチェック */
  const isInputError = (): boolean => {
    const requireInputs: RequireInput[] = [];
    if (requireTopLevel) {
      requireInputs.push({ value: topLevel, ref: topLevelRef });
    }
    return includeInputValidateError(document, intl, requireInputs);
  };

  /** 検索 */
  const handleSearch = async () => {
    if (isInputError()) {
      return;
    }
    showLoading();
    const shapeClassNames: BlueprintSegmentNames[] = [];
    if (isFront) {
      shapeClassNames.push(BlueprintSegmentNames.Front);
    }
    if (isRight) {
      shapeClassNames.push(BlueprintSegmentNames.Right);
    }
    if (isBottom) {
      shapeClassNames.push(BlueprintSegmentNames.Bottom);
    }
    try {
      const condition: BlueprintSearchCondition = {
        file: {
          filename,
          temporaryImage,
          segments: temporarySegmentProperties,
        },
        shape: {
          topX: topLevel,
          classNames: shapeClassNames,
        },
        keyword: {
          text: keywords,
          matchType: matchType,
        },
        amount: {
          leadNumber: {
            value: removeMinus(leadNumber),
            tolerance: {
              lower: removeMinus(leadNumberLower),
              upper: removeMinus(leadNumberUpper),
            },
          },
        },
        size: {
          unit: unit,
          outerL: {
            value: removeMinus(outerL),
            tolerance: {
              lower: removeMinus(outerLLower),
              upper: removeMinus(outerLUpper),
            },
          },
          outerW: {
            value: removeMinus(outerW),
            tolerance: {
              lower: removeMinus(outerWLower),
              upper: removeMinus(outerWUpper),
            },
          },
          outerT: {
            value: removeMinus(outerT),
            tolerance: {
              lower: removeMinus(outerTLower),
              upper: removeMinus(outerTUpper),
            },
          },
          topCVL: {
            value: removeMinus(topCVL),
            tolerance: {
              lower: removeMinus(topCVLLower),
              upper: removeMinus(topCVLUpper),
            },
          },
          topCVW: {
            value: removeMinus(topCVW),
            tolerance: {
              lower: removeMinus(topCVWLower),
              upper: removeMinus(topCVWUpper),
            },
          },
          secondCVL: {
            value: removeMinus(secondCVL),
            tolerance: {
              lower: removeMinus(secondCVLLower),
              upper: removeMinus(secondCVLUpper),
            },
          },
          secondCVW: {
            value: removeMinus(secondCVW),
            tolerance: {
              lower: removeMinus(secondCVWLower),
              upper: removeMinus(secondCVWUpper),
            },
          },
          daL: {
            value: removeMinus(daL),
            tolerance: {
              lower: removeMinus(daLLower),
              upper: removeMinus(daLUpper),
            },
          },
          daW: {
            value: removeMinus(daW),
            tolerance: {
              lower: removeMinus(daWLower),
              upper: removeMinus(daWUpper),
            },
          },
          boardWarp: {
            value: removeMinus(boardWarp),
            tolerance: {
              lower: removeMinus(boardWarpLower),
              upper: removeMinus(boardWarpUpper),
            },
          },
          srWarp: {
            value: removeMinus(srWarp),
            tolerance: {
              lower: removeMinus(srWarpLower),
              upper: removeMinus(srWarpUpper),
            },
          },
          daWarp: {
            value: removeMinus(daWarp),
            tolerance: {
              lower: removeMinus(daWarpLower),
              upper: removeMinus(daWarpUpper),
            },
          },
        },
        attribute: checkedAttributes.map((item) => item.displayName),
      };

      // エンドポイントへリクエストし、ウェイトを掛けた上で結果画面に遷移する
      search({ condition });
      saveBlueprintSearchState(VIEW_ID, condition, myEmail);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      navi('/blueprint/blueprint-search-result');
    } catch (err) {
      error(getExceptionMessage(intl, err));
      throw err;
    } finally {
      hideLoading();
    }
  };

  const removeMinus = (num: string) => {
    const reg = new RegExp('^-0+(\\.0+)?$');
    const newNum = reg.test(num) ? num.replace('-', '') : num;
    const zeroCheck = new RegExp('^0+$');
    return zeroCheck.test(newNum) ? '0' : newNum;
  };

  const handleReset = () => {
    setFilename(null);
    setTemporaryImage(null);
    setTemporarySegmentProperties(null);
    setFront(false);
    setRight(false);
    setBottom(false);
    setTopLevel('');
    setKeywords('');
    setMatchType('or');
    setLeadNumber('');
    setLeadNumberLower('0');
    setLeadNumberUpper('0');
    setUnit('mm');
    setOuterL('');
    setOuterLLower('0');
    setOuterLUpper('0');
    setOuterW('');
    setOuterWLower('0');
    setOuterWUpper('0');
    setOuterT('');
    setOuterTLower('0');
    setOuterTUpper('0');
    setTopCVL('');
    setTopCVLLower('0');
    setTopCVLUpper('0');
    setTopCVW('');
    setTopCVWLower('0');
    setTopCVWUpper('0');
    setSecondCVL('');
    setSecondCVLLower('0');
    setSecondCVLUpper('0');
    setSecondCVW('');
    setSecondCVWLower('0');
    setSecondCVWUpper('0');
    setDaL('');
    setDaLLower('0');
    setDaLUpper('0');
    setDaW('');
    setDaWLower('0');
    setDaWUpper('0');
    setBoardWarp('');
    setBoardWarpLower('0');
    setBoardWarpUpper('0');
    setSrWarp('');
    setSrWarpLower('0');
    setSrWarpUpper('0');
    setDaWarp('');
    setDaWarpLower('0');
    setDaWarpUpper('0');
    // setCheckedAttributes([]);
  };

  const handleFileDelete = (files: FileItem[]) => {
    if (files.length > 0) {
      return;
    }
    setFilename(null);
    setTemporaryImage(null);
    setTemporarySegmentProperties(null);
    setFront(false);
    setRight(false);
    setBottom(false);
    setTopLevel('');
  };

  const handleLoading = (isLoading: boolean) => {
    isLoading ? showLoading() : hideLoading();
  };

  const handleChange = (id: string, checked: boolean) => {
    if (checked) {
      setCheckedAttributes([
        ...checkedAttributes,
        ...displayAttributes.filter((item) => item.id === id),
      ]);
    } else {
      setCheckedAttributes(checkedAttributes.filter((item) => item.id !== id));
    }
  };

  return (
    <Container viewId={VIEW_ID}>
      <div className="BlueprintBlueprintSearch">
        <h2 className="section-head">
          {GetMessageWithIntl(intl, {
            prefixId: VIEW_ID,
            id: 'sectionHeader.image',
          })}
        </h2>
        <div className="condition-section">
          <dl className="detail-condition image">
            <dt>
              {GetMessageWithIntl(intl, {
                prefixId: VIEW_ID,
                id: 'image.imageFile',
              })}
            </dt>
            <dd>
              <div className="input-line">
                <div className="item-group-100">
                  <div className="w-100">
                    <p className="image-file-description-label">
                      {GetMessageWithIntl(intl, {
                        prefixId: VIEW_ID,
                        id: 'image.imageFile.description',
                      })}
                    </p>
                    <FileUploader
                      name="upload"
                      validateOption={{
                        allowedFileExtensions: ['pdf'],
                      }}
                      onUpload={handleFileUpload}
                      onDelete={handleFileDelete}
                      onChangeLoadingState={(isLoading) => {
                        handleLoading(isLoading);
                      }}
                    />
                  </div>
                </div>
              </div>
            </dd>
            <dt>
              {GetMessageWithIntl(intl, {
                prefixId: VIEW_ID,
                id: 'image.shape',
              })}
            </dt>
            <dd>
              <div className="input-line">
                <div className="item-group-100">
                  <div className="w-20">
                    <Checkbox
                      name={'frontCheck'}
                      className=""
                      columns={['frontCheck']}
                      items={[
                        {
                          value: '1',
                          displayName: GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'image.shape.front',
                          }),
                        },
                      ]}
                      value={[isFront ? '1' : '']}
                      onChangeState={(val) => {
                        setFront(val.includes('1'));
                      }}
                      disabled={disableSegmentCondition || !existsFrontSegment}
                    />
                  </div>
                  <div className="w-20">
                    <Checkbox
                      name={'rightCheck'}
                      className=""
                      columns={['rightCheck']}
                      items={[
                        {
                          value: '1',
                          displayName: GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'image.shape.right',
                          }),
                        },
                      ]}
                      value={[isRight ? '1' : '']}
                      onChangeState={(val) => {
                        setRight(val.includes('1'));
                      }}
                      disabled={disableSegmentCondition || !existsRightSegment}
                    />
                  </div>
                  <div className="w-20">
                    <Checkbox
                      name={'bottomCheck'}
                      className=""
                      columns={['bottomCheck']}
                      items={[
                        {
                          value: '1',
                          displayName: GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'image.shape.bottom',
                          }),
                        },
                      ]}
                      value={[isBottom ? '1' : '']}
                      onChangeState={(val) => {
                        setBottom(val.includes('1'));
                      }}
                      disabled={disableSegmentCondition || !existsBottomSegment}
                    />
                  </div>
                  <div className="w-40 segment-button">
                    <CaptionButton
                      name="segment"
                      caption={GetMessageWithIntl(intl, {
                        prefixId: VIEW_ID,
                        id: 'image.segmentDialog',
                      })}
                      buttonType="basic"
                      onClick={() => {
                        setOpenSegmentConfirmation(true);
                      }}
                      disabled={disableSegmentCondition}
                    />
                  </div>
                </div>
              </div>
              <div className="input-line" ref={topLevelRef}>
                <div className="item-group-100">
                  <div className="w-40">
                    <div className="label-with-input">
                      <span className="label">
                        {GetMessageWithIntl(intl, {
                          prefixId: VIEW_ID,
                          id: 'image.shape.topLevel',
                        })}
                      </span>
                      <Textbox
                        name="topLevel"
                        columns={['topLevel']}
                        value={topLevel}
                        validator={validateOnlyNumberWithMaxMin(
                          intl,
                          1,
                          BLUEPRINT_SEARCH_LIMIT
                        )}
                        validateOption={{ required: requireTopLevel }}
                        type="text"
                        onChangeState={setTopLevel}
                        disabled={
                          disableSegmentCondition || !existsSomeSegments
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </dd>
          </dl>
        </div>
        {/* TODO：将来的にライセンスで検索条件を変更する予定 */}
        <h2 className="section-head">
          {GetMessageWithIntl(intl, {
            prefixId: VIEW_ID,
            id: 'sectionHeader.detail',
          })}
        </h2>
        <div className="condition-section">
          <dl className="detail-condition detail">
            <dt>
              {GetMessageWithIntl(intl, {
                prefixId: VIEW_ID,
                id: 'detail.keyword',
              })}
            </dt>
            <dd>
              <div className="input-line">
                <div className="item-group-100">
                  <div className="w-77">
                    <Textbox
                      name="keywords"
                      className="keywords"
                      columns={['keywords']}
                      value={keywords}
                      type="text"
                      onChangeState={setKeywords}
                    />
                  </div>
                  <div className="w-20">
                    <Radio
                      name="searchOption"
                      className="searchOption"
                      value={matchType}
                      items={[
                        {
                          value: 'or',
                          displayName: 'or',
                        },
                        {
                          value: 'and',
                          displayName: 'and',
                        },
                      ]}
                      onChangeState={(val) =>
                        val === 'or' || val === 'and'
                          ? setMatchType(val)
                          : setMatchType('or')
                      }
                    />
                  </div>
                </div>
              </div>
              <p className="search-direction">
                {GetMessageWithIntl(intl, {
                  prefixId: VIEW_ID,
                  id: 'detail.searchDirection',
                })}
              </p>
              <p className="search-direction-caution">
                {GetMessageWithIntl(intl, {
                  prefixId: VIEW_ID,
                  id: 'detail.searchDirection.caution',
                })}
              </p>
            </dd>
            <dt>
              {GetMessageWithIntl(intl, {
                prefixId: VIEW_ID,
                id: 'detail.amount',
              })}
            </dt>
            <dd>
              <div className="indent size">
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-33">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.amount.leadNumber',
                          })}
                        </p>
                        <Textbox
                          name="leadNumber"
                          className=""
                          columns={['leadNumber']}
                          value={leadNumber}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setLeadNumber}
                        />
                      </div>
                    </div>
                    <div className="w-66">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.errorMargin',
                          })}
                        </p>
                        <p className="prefix">-</p>
                        <Textbox
                          name="leadNumber.min"
                          className=""
                          columns={['leadNumber.min']}
                          value={leadNumberLower}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setLeadNumberLower}
                        />
                        <p>/</p>
                        <Textbox
                          name="leadNumber.max"
                          className=""
                          columns={['leadNumber.max']}
                          value={leadNumberUpper}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setLeadNumberUpper}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </dd>
            <dt>
              {GetMessageWithIntl(intl, {
                prefixId: VIEW_ID,
                id: 'detail.size',
              })}
            </dt>
            <dd>
              <div className="input-line">
                <div className="item-group-100">
                  <div className="w-33">
                    <Radio
                      name="unit"
                      className="unit"
                      value={unit}
                      items={[
                        {
                          value: 'mm',
                          displayName: 'mm',
                        },
                        {
                          value: 'inch',
                          displayName: 'inch',
                        },
                      ]}
                      onChangeState={(val) =>
                        val === 'mm' || val === 'inch'
                          ? setUnit(val)
                          : setUnit('mm')
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="indent size">
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-33">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.size.outerL',
                          })}
                        </p>
                        <Textbox
                          name="outerL"
                          className=""
                          columns={['outerL']}
                          value={outerL}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setOuterL}
                        />
                      </div>
                    </div>
                    <div className="w-66">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.errorMargin',
                          })}
                        </p>
                        <p className="prefix">-</p>
                        <Textbox
                          name="outerL.min"
                          className=""
                          columns={['outerL.min']}
                          value={outerLLower}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setOuterLLower}
                        />
                        <p>/</p>
                        <Textbox
                          name="outerL.max"
                          className=""
                          columns={['outerL.max']}
                          value={outerLUpper}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setOuterLUpper}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-33">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.size.outerW',
                          })}
                        </p>
                        <Textbox
                          name="outerW"
                          className=""
                          columns={['outerW']}
                          value={outerW}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setOuterW}
                        />
                      </div>
                    </div>
                    <div className="w-66">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.errorMargin',
                          })}
                        </p>
                        <p className="prefix">-</p>
                        <Textbox
                          name="outerW.min"
                          className=""
                          columns={['outerW.min']}
                          value={outerWLower}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setOuterWLower}
                        />
                        <p>/</p>
                        <Textbox
                          name="outerW.max"
                          className=""
                          columns={['outerW.max']}
                          value={outerWUpper}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setOuterWUpper}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-33">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.size.outerT',
                          })}
                        </p>
                        <Textbox
                          name="outerT"
                          className=""
                          columns={['outerT']}
                          value={outerT}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setOuterT}
                        />
                      </div>
                    </div>
                    <div className="w-66">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.errorMargin',
                          })}
                        </p>
                        <p className="prefix">-</p>
                        <Textbox
                          name="outerT.min"
                          className=""
                          columns={['outerT.min']}
                          value={outerTLower}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setOuterTLower}
                        />
                        <p>/</p>
                        <Textbox
                          name="outerT.max"
                          className=""
                          columns={['outerT.max']}
                          value={outerTUpper}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setOuterTUpper}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-33">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.size.topCVL',
                          })}
                        </p>
                        <Textbox
                          name="topCVL"
                          className=""
                          columns={['topCVL']}
                          value={topCVL}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setTopCVL}
                        />
                      </div>
                    </div>
                    <div className="w-66">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.errorMargin',
                          })}
                        </p>
                        <p className="prefix">-</p>
                        <Textbox
                          name="topCVL.min"
                          className=""
                          columns={['topCVL.min']}
                          value={topCVLLower}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setTopCVLLower}
                        />
                        <p>/</p>
                        <Textbox
                          name="topCVL.max"
                          className=""
                          columns={['topCVL.max']}
                          value={topCVLUpper}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setTopCVLUpper}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-33">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.size.topCVW',
                          })}
                        </p>
                        <Textbox
                          name="topCVW"
                          className=""
                          columns={['topCVW']}
                          value={topCVW}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setTopCVW}
                        />
                      </div>
                    </div>
                    <div className="w-66">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.errorMargin',
                          })}
                        </p>
                        <p className="prefix">-</p>
                        <Textbox
                          name="topCVW.min"
                          className=""
                          columns={['topCVW.min']}
                          value={topCVWLower}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setTopCVWLower}
                        />
                        <p>/</p>
                        <Textbox
                          name="topCVW.max"
                          className=""
                          columns={['topCVW.max']}
                          value={topCVWUpper}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setTopCVWUpper}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-33">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.size.secondCVL',
                          })}
                        </p>
                        <Textbox
                          name="secondCVL"
                          className=""
                          columns={['secondCVL']}
                          value={secondCVL}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setSecondCVL}
                        />
                      </div>
                    </div>
                    <div className="w-66">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.errorMargin',
                          })}
                        </p>
                        <p className="prefix">-</p>
                        <Textbox
                          name="secondCVL.min"
                          className=""
                          columns={['secondCVL.min']}
                          value={secondCVLLower}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setSecondCVLLower}
                        />
                        <p>/</p>
                        <Textbox
                          name="secondCVL.max"
                          className=""
                          columns={['secondCVL.max']}
                          value={secondCVLUpper}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setSecondCVLUpper}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-33">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.size.secondCVW',
                          })}
                        </p>
                        <Textbox
                          name="secondCVW"
                          className=""
                          columns={['secondCVW']}
                          value={secondCVW}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setSecondCVW}
                        />
                      </div>
                    </div>
                    <div className="w-66">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.errorMargin',
                          })}
                        </p>
                        <p className="prefix">-</p>
                        <Textbox
                          name="secondCVW.min"
                          className=""
                          columns={['secondCVW.min']}
                          value={secondCVWLower}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setSecondCVWLower}
                        />
                        <p>/</p>
                        <Textbox
                          name="secondCVW.max"
                          className=""
                          columns={['secondCVW.max']}
                          value={secondCVWUpper}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setSecondCVWUpper}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-33">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.size.daL',
                          })}
                        </p>
                        <Textbox
                          name="daL"
                          className=""
                          columns={['daL']}
                          value={daL}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setDaL}
                        />
                      </div>
                    </div>
                    <div className="w-66">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.errorMargin',
                          })}
                        </p>
                        <p className="prefix">-</p>
                        <Textbox
                          name="daL.min"
                          className=""
                          columns={['daL.min']}
                          value={daLLower}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setDaLLower}
                        />
                        <p>/</p>
                        <Textbox
                          name="daL.max"
                          className=""
                          columns={['daL.max']}
                          value={daLUpper}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setDaLUpper}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-33">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.size.daW',
                          })}
                        </p>
                        <Textbox
                          name="daW"
                          className=""
                          columns={['daW']}
                          value={daW}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setDaW}
                        />
                      </div>
                    </div>
                    <div className="w-66">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.errorMargin',
                          })}
                        </p>
                        <p className="prefix">-</p>
                        <Textbox
                          name="daW.min"
                          className=""
                          columns={['daW.min']}
                          value={daWLower}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setDaWLower}
                        />
                        <p>/</p>
                        <Textbox
                          name="daW.max"
                          className=""
                          columns={['daW.max']}
                          value={daWUpper}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setDaWUpper}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-33">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.size.boardWarp',
                          })}
                        </p>
                        <Textbox
                          name="boardWarp"
                          className=""
                          columns={['boardWarp']}
                          value={boardWarp}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setBoardWarp}
                        />
                      </div>
                    </div>
                    <div className="w-66">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.errorMargin',
                          })}
                        </p>
                        <p className="prefix">-</p>
                        <Textbox
                          name="boardWarp.min"
                          className=""
                          columns={['boardWarp.min']}
                          value={boardWarpLower}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setBoardWarpLower}
                        />
                        <p>/</p>
                        <Textbox
                          name="boardWarp.max"
                          className=""
                          columns={['boardWarp.max']}
                          value={boardWarpUpper}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setBoardWarpUpper}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-33">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.size.srWarp',
                          })}
                        </p>
                        <Textbox
                          name="srWarp"
                          className=""
                          columns={['srWarp']}
                          value={srWarp}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setSrWarp}
                        />
                      </div>
                    </div>
                    <div className="w-66">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.errorMargin',
                          })}
                        </p>
                        <p className="prefix">-</p>
                        <Textbox
                          name="srWarp.min"
                          className=""
                          columns={['srWarp.min']}
                          value={srWarpLower}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setSrWarpLower}
                        />
                        <p>/</p>
                        <Textbox
                          name="srWarp.max"
                          className=""
                          columns={['srWarp.max']}
                          value={srWarpUpper}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setSrWarpUpper}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="input-line">
                  <div className="item-group-100">
                    <div className="w-33">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.size.daWarp',
                          })}
                        </p>
                        <Textbox
                          name="daWarp"
                          className=""
                          columns={['daWarp']}
                          value={daWarp}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setDaWarp}
                        />
                      </div>
                    </div>
                    <div className="w-66">
                      <div className="label-with-input-with-space">
                        <p className="label">
                          {GetMessageWithIntl(intl, {
                            prefixId: VIEW_ID,
                            id: 'detail.errorMargin',
                          })}
                        </p>
                        <p className="prefix">-</p>
                        <Textbox
                          name="daWarp.min"
                          className=""
                          columns={['daWarp.min']}
                          value={daWarpLower}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setDaWarpLower}
                        />
                        <p>/</p>
                        <Textbox
                          name="daWarp.max"
                          className=""
                          columns={['daWarp.max']}
                          value={daWarpUpper}
                          type="text"
                          validator={getSizeConditionValidator()}
                          onChangeState={setDaWarpUpper}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </dd>
          </dl>
        </div>
        <h2 className="section-head">表示属性</h2>
        <div className="condition-section">
          <dl className="detail-condition attributes">
            <dt>属性情報</dt>
            <dd>
              <div className="condition-list-item-detail">
                {displayAttributes.map((attribute) => (
                  <React.Fragment key={attribute.id}>
                    <ConditionCheckbox
                      value={attribute.id || ''}
                      checked={checkedAttributes.some(
                        (item) => item.id === attribute.id
                      )}
                      onChange={(id, checked) =>
                        handleChange(attribute.id || '', checked)
                      }
                      label={attribute.displayName || ''}
                    />
                  </React.Fragment>
                ))}
              </div>
            </dd>
          </dl>
        </div>
        {/* フッター */}
        <div className="footer">
          <div className="footer-contents">
            <CaptionButton
              buttonType="basic"
              name="search"
              caption="検索"
              onClick={handleSearch}
            />
            <CaptionButton
              name="reset"
              className="fetch-reset-button"
              caption="条件解除"
              onClick={handleReset}
            />
          </div>
        </div>
      </div>
      <>
        {/* セグメント確認ダイアログ */}
        <SegmentConfirmationDialog
          key={isOpenSegmentConfirmation ? 1 : 0}
          isOpen={isOpenSegmentConfirmation}
          messageOption={{
            headerLabel: {
              prefixId: 'DIALOG_TITLE',
              id: 'SegmentConfirmationDialog',
            },
          }}
          inputOption={{
            assetId: temporaryImage?.assetId ?? '',
            mode: 'searchCondition',
            segment: {
              imageProperty: {
                imageAssetId: temporaryImage?.assetId,
                imageHeight: temporaryImage?.height,
                imageWidth: temporaryImage?.width,
                thumbnailAssetId: temporaryImage?.signedUrl,
              },
              segmentPropertys: (temporarySegmentProperties ?? []).map(
                (item) => ({ ...item.segmentProperty })
              ),
            },
          }}
          onDecision={handleSaveSegment}
          onCancel={() => setOpenSegmentConfirmation(false)}
        />
      </>
      <Toast />
    </Container>
  );
};

/** セグメント名に対応するセグメントがあるかどうか */
const hasSegment = (
  segments:
    | mtechnavi.api.blueprint.ITemporarySegmentProperty[]
    | null
    | undefined,
  segmentName: BlueprintSegmentNames
): boolean => {
  return (segments ?? []).some(
    (item) => item.segmentProperty?.segmentName === segmentName
  );
};
